import { useState, useEffect } from 'react';
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading';
import { useAuth } from "../../context/AuthProvider";
import { 
  Phone, 
  User, 
  Calendar, 
  AlertCircle,
  Plus,
  Ban,
  Trash2,
} from 'lucide-react';
import { LiaUserSlashSolid } from "react-icons/lia";
import DeleteModal from '../../cors/DeleteModal';

export default function BlackListPage() {
  const { token ,role ,roles } = useAuth();
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);


  useEffect(() => {
    fetchBlacklist();
  }, []);

  const fetchBlacklist = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/black-list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setBlacklist(data.data);
      }
    } catch (error) {
      console.error('Error fetching blacklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleConfirmUnblock = async () => {
    if (!selectedItem) return;
    
    setIsModalOpen(false);
    try {
      const response = await fetch(`${BASE_URL}/api/black-list/${selectedItem.phone}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
  
      const data = await response.json()
  
      if (data.success) {
        toast.success('Client débloqué avec succès')
        fetchBlacklist()
        setSelectedItem(null);
      } else {
        toast.error(data.message || 'Erreur lors du déblocage')
      }
    } catch (error) {
      toast.error('Une erreur est survenue')
    } 
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mx-auto px-6 py-8 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 max pt-12">
         <div>
           <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
             Black list
           </h1>
           <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
             Gérez la liste noire des clients, numéros ou adresses bloqués afin de prévenir les commandes à risque ou frauduleuses.
           </p>
         </div>
       </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loading />
        </div>
      ) : blacklist.length === 0 ? (
        <div className="text-center py-20">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            {searchTerm ? 'Aucun résultat trouvé' : 'Liste noire vide'}
          </h3>
          <p className="text-gray-500 text-xs">
            {searchTerm 
              ? 'Aucune entrée ne correspond à votre recherche.'
              : 'Commencez par ajouter des clients ou numéros à la liste noire.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {blacklist.map((item) => (
            <div key={item.id} className="bg-white rounded-xl border border-gray-200 px-4 pt-4 pb-2 shadow-sm hover:shadow-md transition">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className='p-2 rounded-lg bg-red-100'>
                      <LiaUserSlashSolid className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-gray-800">
                      { item?.name }
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {item.phone && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone className="w-3.5 h-3.5" />
                    <span className="font-medium text-xs">{item.phone}</span>
                  </div>
                )}

                {item.reason && (
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-4 h-4 text-gray-400" />
                    <div>
                      <span className='inline-block px-3 py-1 text-xs font-medium rounded-full border'>
                        {item.reason}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-[11px] text-gray-500">
                      <Calendar className="w-3.5 h-3.5" />
                      Bloqué le {formatDate(item.created_at)}
                    </div>
                    {
                      role === 1 || (role === 2 && roles.remove_blacklist === 1) ?
                    <button 
                      onClick={() => handleUnblockClick(item)} 
                      className="px-2 cursor-pointer font-medium text-[10px] py-1.5 bg-red-700 text-white rounded-md hover:bg-red-800 transition flex items-center gap-0.5"
                    >
                      <Ban className="w-3 h-3" />
                      Débloquer
                    </button>
                    :''
                    }
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedItem && (
        <DeleteModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedItem(null);
          }}
          onConfirm={handleConfirmUnblock}
          title="Débloquer le client"
          message="Êtes-vous sûr de vouloir le retirer de la liste noire ?"
          itemName={selectedItem?.name}
        />
      )}
    </div>
  );
}